import React, { type ReactNode } from "react";
import { SelectableAssetCard } from "../SelectableAssetCard/SelectableAssetCard";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components/textTruncators/TextTruncators";
import { Ref } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCodeBranch, faBell } from "@fortawesome/pro-solid-svg-icons";
import CardThumbnail from "../cardThumbnail/CardThumbnail";
import cn from "classnames";
import { type BranchingQuestion } from "interfaces";
import dateTimeUtils from "../../../utils/dateTimeUtils";
import ImageAspectRatio from "enums/imageAspectRatio";
import type DragNDropItemTypes from "enums/dragNDropItemTypes";
import { EntityType } from "features/Library/Flows/Designer/types";
import { useDraggableAssetCard } from "hooks/useDraggableAssetCard";
import { SendTypeEnum } from "enums/sendTypeEnum";

import "./DesignerAssetCard.scss";
import { HiddenContent } from "components/HiddenContent/HiddenContent";

const assetTypeDisplayName = (type: EntityType): string => {
  switch (type) {
    case EntityType.Flow:
      return "Flow";
    case EntityType.Video:
      return "Video";
    case EntityType.EmbeddedVideo:
      return "Embedded Video";
    case EntityType.Assessment:
      return "Assessment";
    case EntityType.Survey:
      return "Survey";
    case EntityType.Pdf:
      return "PDF";
    case EntityType.ExternalEvent:
      return "Event";
    case EntityType.Link:
      return "Link";
    case EntityType.Email:
      return "Email";
    case EntityType.Message:
      return "Message";
    case EntityType.Campaign:
      return "Campaign";
    case EntityType.SectionHeader:
      return "Section Header";
    case EntityType.FlowEnd:
      return "End of Flow";
    default:
      return String(type);
  }
};

export type Props = {
  id: number | string;
  title: string;
  isDraggable: boolean;
  assetType: EntityType;
  thumbnailUrl: string | ReactNode;
  dragNDropItemType: DragNDropItemTypes;
  dataHookType: string;
  publisher?: string;
  className?: string;
  onClick?: () => void;
  hasReminders?: boolean;
  durationInSeconds?: number;
  branchingQuestion?: BranchingQuestion;
  questionsCount?: string;
  hideTitle?: boolean;
  visibility?: boolean;
};

export const DesignerAssetCard: React.FC<Props> = ({
  id,
  assetType,
  title,
  className,
  thumbnailUrl,
  durationInSeconds,
  hasReminders,
  branchingQuestion,
  questionsCount,
  isDraggable,
  dragNDropItemType,
  publisher,
  dataHookType,
  hideTitle,
  visibility,
  ...props
}) => {
  const displayName = assetTypeDisplayName(assetType);

  const [dragRef, isSelected, { handleClick }] = useDraggableAssetCard({
    type: dragNDropItemType,
    item: {
      id,
      title,
      thumbnailUrl,
      publisher,
      elementType: assetType,
      durationInSeconds: assetType === EntityType.Video ? durationInSeconds : 0,
      type: dragNDropItemType,
      questionsCount,
      visibility,
      branchingQuestion,
      bag: {
        SendType: assetType === EntityType.Message ? SendTypeEnum.Teams : undefined,
      },
    },
  });

  const selectableDesignerAssetCard = (
    <SelectableAssetCard
      key={id}
      className={cn("designer-asset-card", className, { "asset-draggable-item": isDraggable })}
      tabIndex="0"
      data-hook-type={dataHookType}
      data-hook-id={id}
      {...(isDraggable && { isSelected })}
      {...(isDraggable && { onClick: handleClick })}
      {...props}
    >
      <div className="thumbnail">
        {typeof thumbnailUrl === "string" ? (
          <CardThumbnail
            thumbnailUrl={thumbnailUrl}
            draggable={false}
            aspectRatio={ImageAspectRatio.HD_24X10}
            isHidden={!visibility}
          />
        ) : (
          <>
            {thumbnailUrl}
            {visibility !== undefined && <HiddenContent variant="card" isHidden={!visibility} />}
          </>
        )}
      </div>
      <div className="type-block">
        {branchingQuestion && <FontAwesomeIcon icon={faCodeBranch} size="sm" />}
        {hasReminders && <FontAwesomeIcon icon={faBell} size="sm" />}
        <div className={"typeSection"}>
          <span className="text">{displayName}</span>
          {durationInSeconds && (
            <span className={"text"}>{dateTimeUtils.durationSecondsToTime(durationInSeconds)}</span>
          )}
        </div>
      </div>
      <div className="content card-content">
        <div
          className={cn("header card-header", {
            hideTitle,
          })}
        >
          <Tooltip target={<TextTruncate lines={2}>{title}</TextTruncate>} content={title} />
        </div>
      </div>
    </SelectableAssetCard>
  );
  return isDraggable ? <Ref innerRef={dragRef}>{selectableDesignerAssetCard}</Ref> : selectableDesignerAssetCard;
};

export default DesignerAssetCard;
